import React from "react";
import download from "../../assets/images/download.png";
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useState, useEffect } from 'react';
import axiosInstance from '../../helpers/axios';
import { TablePagination } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { userActions } from '../../actions/user.actions';
import { Tooltip as ReactTooltip } from "react-tooltip";
import { ChevronDown, ChevronUp } from 'lucide-react';

const toSafeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const mergeTotalCollectionRecords = (
  arrearsList = [],
  currentList = [],
  prepaymentList = []
) => {
  const mergedMap = new Map();

  const upsertRecord = (item, type) => {
    if (!item) return;
    const key = item.loan_id ?? item.legacy_loan_id ?? item.mobile ?? `${item.userName || ""}-${item.mobile || ""}`;
    if (!key) return;

    const existing = mergedMap.get(key) || {};
    const updatedRecord = {
      ...existing,
      loan_id: item.loan_id ?? existing.loan_id ?? null,
      legacy_loan_id: item.legacy_loan_id ?? existing.legacy_loan_id ?? '-',
      userName: item.userName ?? existing.userName ?? '-',
      mobile: item.mobile ?? existing.mobile ?? '-',
      bucketing: item.bucketing ?? existing.bucketing ?? '-',
      status_name: item.status_name ?? existing.status_name ?? '-',
      paidBucket: item.paidBucket ?? existing.paidBucket ?? '-',
    };

    if (type === 'arrears') {
      updatedRecord.opening_arrears = toSafeNumber(item.opening_arrears ?? existing.opening_arrears ?? 0);
      updatedRecord.arrearCollection = toSafeNumber(item.arrearCollection ?? existing.arrearCollection ?? 0);
    }

    if (type === 'current') {
      updatedRecord.currentDemand = toSafeNumber(item.currentDemand ?? existing.currentDemand ?? 0);
      updatedRecord.paid_emi_amount = toSafeNumber(item.paid_emi_amount ?? existing.paid_emi_amount ?? 0);
    }

    if (type === 'prepayment') {
      updatedRecord.prepaid = toSafeNumber(item.prepaid ?? existing.prepaid ?? 0);
    }

    mergedMap.set(key, updatedRecord);
  };

  arrearsList.forEach((item) => upsertRecord(item, 'arrears'));
  currentList.forEach((item) => upsertRecord(item, 'current'));
  prepaymentList.forEach((item) => upsertRecord(item, 'prepayment'));

  return Array.from(mergedMap.values()).map((record) => ({
    ...record,
    combinedTotalCollection:
      (record.paid_emi_amount || 0) +
      (record.arrearCollection || 0) +
      (record.prepaid || 0),
  }));
};
 

const DemandReports = () =>{
  const user = null;
  const productTypeSelected = useSelector((state) => state.ProductTypeFilter.selectedProductType);
  const [demandReport,setDemandReport] = useState([]);
  const [isLoading, setIsLoading] = useState(false); 
   const [isLoadingTable, setIsLoadingTable] = useState(false); 
  const [selectedCell, setSelectedCell] = useState({ row: null, col: null });
  const [apiData, setApiData] = useState([]); 
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
    const dispatch = useDispatch();


  useEffect(() => {
    setIsLoading(true)
    let queryParams = {};

    if (productTypeSelected && productTypeSelected !== "all") {
      queryParams.loanType = Number(productTypeSelected);
    }

    axiosInstance.get('/analytics/demand-collection', {
      params: queryParams,
      headers: {
      }
    })
      .then(function (response) {
      setDemandReport(response?.data?.data);
        setIsLoading(false);
        
      })
      .catch(function (error) {
      });

  }, [productTypeSelected]);
     

  const fetchListData = (endpoint,item) => {
    setIsLoadingTable(true);
    setPage(0);
    const formatMonthForAPI = (monthStr) => {
    if (!monthStr) return null;
    const [year, monthText] = monthStr.split(" ");
    const months = {
      Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
      Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12"
    };
    return `${year}-${months[monthText]}-01`;
  };
  
  const formattedDate = formatMonthForAPI(item?.month);
    let queryParams = {
      date: formattedDate,
    };

    if (productTypeSelected && productTypeSelected !== "all") {
      queryParams.loanType = Number(productTypeSelected);
    }
    axiosInstance
      .get(endpoint, {
        params: queryParams
      })
      .then((response) => {
        setApiData(response?.data?.data || []);
        setIsLoadingTable(false);
      })
      .catch((error) => {
        setIsLoadingTable(false);
      });
  };

  const fetchTotalCollectionDetails = async () => {
    setIsLoadingTable(true);
    setPage(0);
    try {
      let queryParams = {};

      if (productTypeSelected && productTypeSelected !== "all") {
        queryParams.loanType = Number(productTypeSelected);
      }
      const [arrearsRes, currentRes, prepaymentRes] = await Promise.all([
        axiosInstance.get('/analytics/get-arrears-data', { params: queryParams }),
        axiosInstance.get('/analytics/get-currdemand-data', { params: queryParams }),
        axiosInstance.get('/analytics/get-prepayment-data', { params: queryParams }),
      ]);

      const merged = mergeTotalCollectionRecords(
        arrearsRes?.data?.data || [],
        currentRes?.data?.data || [],
        prepaymentRes?.data?.data || []
      );

      setApiData(merged);
    } catch (error) {
    } finally {
      setIsLoadingTable(false);
    }
  };

  const handleArrearsClick = (row, col,item) => {
    if (selectedCell.row === row && selectedCell.col === col) {
      setSelectedCell({ row: null, col: null });
      setApiData([]);
      return;
    }

    setSelectedCell({ row, col });

    if (col === "opening_arrears" || col === "arrearCollection") {
      fetchListData('/analytics/get-arrears-data',item);
      return;
    }

    if (col === "currentDemand") {
      fetchListData('/analytics/get-currdemand-data',item);
      return;
    }

    if (col === "Prepayments") {
      fetchListData('/analytics/get-prepayment-data',item);
      return;
    }

    if (col === "totalCollection") {
      fetchTotalCollectionDetails();
    }
  };

   
  function formatNumberWithCommas(number) {
    return number.toLocaleString('en-IN'); 
  }

  const handleDownloadList=()=>{
    const currentDate = new Date();
    const day = String(currentDate.getDate()).padStart(2, '0');
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const year = String(currentDate.getFullYear());
    const formattedDate = `${day}-${month}-${year}`;
    document.getElementById("downloadButton").disabled=true;
    document.getElementById("downloadButton").innerText="Wait....";
    document.getElementById("downloadButton").style.backgroundColor="#D22129";
    document.getElementById("downloadButton").style.color= "white";
    document.getElementById("downloadButton").style.fontFamily= "Inter-Medium";
    document.getElementById("downloadButton").style.fontSize= "15px";
    document.getElementById("downloadButton").style.width= "130px";
    document.getElementById("downloadButton").style.cursor= "pointer";
    document.getElementById("downloadButton").style.height= "34px";
    document.getElementById("downloadButton").style.borderRadius= "5px";
    document.getElementById("downloadButton").style.textAlign= "center";
    // document.getElementById("downloadButton").style.marginLeft="150px";
    document.getElementById("downloadButton").style.backgroundImage = "none"

    let queryParams = {}
    if (productTypeSelected && productTypeSelected !== "all") {
      queryParams.loanType = Number(productTypeSelected);
    }
    axiosInstance.get("/excel/demand-collection", {
      params: queryParams,
      responseType: 'arraybuffer',
      headers: {
      }
  }).then((res)=>{
    setTimeout(() => {
      document.getElementById("downloadButton").disabled=false
      document.getElementById("downloadButton").innerText="Export";
      document.getElementById("downloadButton").style.paddingLeft = "30px";
      document.getElementById("downloadButton").style.backgroundColor="#D22129";
      document.getElementById("downloadButton").style.color= "white";
      document.getElementById("downloadButton").style.fontFamily= "Inter-Medium";
      document.getElementById("downloadButton").style.fontSize= "15px";
      document.getElementById("downloadButton").style.width= "130px";
      document.getElementById("downloadButton").style.cursor= "pointer";
      document.getElementById("downloadButton").style.height= "34px";
      document.getElementById("downloadButton").style.borderRadius= "5px";
      document.getElementById("downloadButton").style.textAlign= "center";
      // document.getElementById("downloadButton").style.marginLeft= "30px";
      document.getElementById("downloadButton").style.backgroundImage = `url(${download})`
      document.getElementById("downloadButton").style.backgroundRepeat  ='no-repeat' 
      // document.getElementById("downloadButton").style.backgroundPositionY  ='20px' 
      document.getElementById("downloadButton").style.backgroundSize  ='15px' 
      document.getElementById("downloadButton").style.backgroundPosition  ='10px 10px'

    }, 2000);
    const blob = new Blob([res.data], { type: "application/xlsx", });
    const url= window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href =url
    link.download = `${formattedDate}.xlsx`;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  }
    
  ).catch((error)=>{
    alert("Error Downloading")
    document.getElementById("downloadButton").innerText="Error..";
    document.getElementById("downloadButton").style.backgroundColor="#D22129";
    document.getElementById("downloadButton").disabled=false
      document.getElementById("downloadButton").style.paddingLeft = "30px";
      document.getElementById("downloadButton").style.backgroundColor="#D22129";
      document.getElementById("downloadButton").style.color= "white";
      document.getElementById("downloadButton").style.fontFamily= "Inter-Medium";
      document.getElementById("downloadButton").style.fontSize= "15px";
      document.getElementById("downloadButton").style.width= "130px";
      document.getElementById("downloadButton").style.cursor= "pointer";
      document.getElementById("downloadButton").style.height= "34px";
      document.getElementById("downloadButton").style.borderRadius= "5px";
      document.getElementById("downloadButton").style.textAlign= "center";
      // document.getElementById("downloadButton").style.marginLeft= "30px";
      document.getElementById("downloadButton").style.backgroundImage = `url(${download})`
      document.getElementById("downloadButton").style.backgroundRepeat  ='no-repeat' 
      // document.getElementById("downloadButton").style.backgroundPositionY  ='20px' 
      document.getElementById("downloadButton").style.backgroundSize  ='15px' 
      document.getElementById("downloadButton").style.backgroundPosition  ='10px 10px'
    setTimeout(() => {
      document.getElementById("downloadButton").disabled=false
      document.getElementById("downloadButton").innerText="Export";
      // document.getElementById("downloadButton").style.paddingLeft = "30px";
      document.getElementById("downloadButton").style.backgroundColor="#D22129";
      document.getElementById("downloadButton").style.color= "white";
      document.getElementById("downloadButton").style.fontFamily= "Inter-Medium";
      document.getElementById("downloadButton").style.fontSize= "15px";
      document.getElementById("downloadButton").style.width= "130px";
      document.getElementById("downloadButton").style.cursor= "pointer";
      document.getElementById("downloadButton").style.height= "34px";
      document.getElementById("downloadButton").style.borderRadius= "5px";
      document.getElementById("downloadButton").style.textAlign= "center";
      // document.getElementById("downloadButton").style.marginLeft= "30px";
      document.getElementById("downloadButton").style.backgroundImage = `url(${download})`
      document.getElementById("downloadButton").style.backgroundRepeat  ='no-repeat' 
      // document.getElementById("downloadButton").style.backgroundPositionY  ='20px' 
      document.getElementById("downloadButton").style.backgroundSize  ='20px' 
      document.getElementById("downloadButton").style.backgroundPosition  ='15px 10px'
    }, 1000);
  })
  } 

      const paginatedData =
  rowsPerPage > 0
    ? apiData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : apiData; 

      const baseParentCellStyle = {
        fontSize: "14px",
        fontFamily: "Inter",
        color: "#667085",
        padding: "12px 16px",
        textAlign: "left",
      };

      const interactiveCellBorderStyle = {
        borderTop: "none",
        borderBottom: "1px solid #d1d5db",
        borderLeft: "none",
        borderRight: "none",
      };

      const handleLoanProfile = (loan_id,loanId)=>{
             dispatch({ type: 'SET_LOAN_CONTEXT', payload: { loanid: loan_id, legacyLoanId: loanId } });
             const urlUsers = `/loan-repayment/profile?loanId=${loan_id}`;
             const callDetailsUrl = `/call-history/details?loanId=${loan_id}`;
             const urlGetReminder=`/call-history/reminder-details?loanId=${loan_id}`
             const emihistoryUrl=`/emis/for-loan?loanId=${loan_id}`

             dispatch(userActions.fetchCallDetails(callDetailsUrl, user),[])
             dispatch(userActions.fetchLoanDetails(urlUsers, user),[])
             dispatch(userActions.getReminder(urlGetReminder))
             dispatch(userActions.getEmiHistory(emihistoryUrl,user))


         window.open(`/Loanprofile?loanId=${loan_id}&legacyLoanId=${loanId}`, "_blank");  }
    
       function formatNumberWithCommas(number) {
        return number?.toLocaleString('en-IN'); 
      }

      const parseMonthLabelToDate = (label) => {
        if (!label) return new Date(0);
        const [yearStr, monthStr] = label.split(" ");
        const monthMap = {
          Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
          Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
        };
        const monthIndex = monthMap[monthStr] ?? 0;
        return new Date(Number(yearStr), monthIndex, 1);
      };

      const latestDemandReports = [...demandReport]
        .sort((a, b) => parseMonthLabelToDate(b.month) - parseMonthLabelToDate(a.month))
        .slice(0, 12);


       const handleDownloadCollectionList = () => {
  const currentDate = new Date();
  const day = String(currentDate.getDate()).padStart(2, "0");
  const month = String(currentDate.getMonth() + 1).padStart(2, "0");
  const year = String(currentDate.getFullYear());
  const formattedDate = `${day}-${month}-${year}`;

  const btn = document.getElementById("downloadButton");
         let queryParams = {};

         if (productTypeSelected && productTypeSelected !== "all") {
           queryParams.loanType = Number(productTypeSelected);
         }
  btn.disabled = true;
  btn.innerText = "Wait....";
  btn.style.backgroundColor = "#D22129";
  btn.style.color = "white";

  if (selectedCell.col === "opening_arrears" || selectedCell.col === "arrearCollection") {
    axiosInstance
      .get("/excel/get-arrears-data", {
        params: queryParams,
        responseType: "arraybuffer",
      })
      .then((res) => {
        // download Excel
        const blob = new Blob([res.data], { type: "application/xlsx" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${formattedDate}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // reset button after 2s
        setTimeout(() => {
          btn.disabled = false;
          btn.innerText = "Export";
          btn.style.backgroundImage = `url(${download})`;
          btn.style.backgroundRepeat = "no-repeat";
          btn.style.backgroundSize = "15px";
          btn.style.backgroundPosition = "10px 10px";
        }, 2000);
      })
      .catch((error) => {
        console.error(error);
        btn.disabled = false;
        btn.innerText = "Error..";
      });
  }

  if (selectedCell.col === "currentDemand") {
    axiosInstance
      .get("/excel/get-currdemand-data", {
        params: queryParams,
        responseType: "arraybuffer",
      })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/xlsx" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${formattedDate}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => {
          btn.disabled = false;
          btn.innerText = "Export";
          btn.style.backgroundImage = `url(${download})`;
          btn.style.backgroundRepeat = "no-repeat";
          btn.style.backgroundSize = "15px";
          btn.style.backgroundPosition = "10px 10px";
        }, 2000);
      })
      .catch((error) => {
        console.error(error);
        btn.disabled = false;
        btn.innerText = "Error..";
      });
  }

    if (selectedCell.col === "Prepayments") {
    axiosInstance
      .get("/excel/get-prepayment-data", {
        params: queryParams,
        responseType: "arraybuffer",
      })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/xlsx" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${formattedDate}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => {
          btn.disabled = false;
          btn.innerText = "Export";
          btn.style.backgroundImage = `url(${download})`;
          btn.style.backgroundRepeat = "no-repeat";
          btn.style.backgroundSize = "15px";
          btn.style.backgroundPosition = "10px 10px";
        }, 2000);
      })
      .catch((error) => {
        console.error(error);
        btn.disabled = false;
        btn.innerText = "Error..";
      });
  }
};

const renderBreakdownTooltip = (row, type, tooltipId) => {
  let principleVal = null;
  let interestVal = null;

  if (type === "opening_arrears") {
    principleVal = row.sumOfPrincipleArrears;
    interestVal = row.sumOfInterestArrears;
  } else if (type === "currentDemand") {
    principleVal = row.principleDemand;
    interestVal = row.InterestDemand;
  } else if (type === "currentCollection") {
    principleVal = row.principleCollection;
    interestVal = row.interestCollection;
  } else if (type === "arrearCollection") {
    principleVal = row.arrearPrincipleCollection;
    interestVal = row.arrearInterestCollection;
  } else if (type === "Prepayments") {
    principleVal = row.principlePrepaid;
    interestVal = row.interestPrepaid;
  } if (type === "totaldemand") {
    principleVal = row.totalPrincipleDemand;
    interestVal = row.totalInterestDemand;
  }
   if (type === "totalCollectiondemand") {
    principleVal = row.totalPrinciple;
    interestVal = row.totalInterest;
  }

  return (
    <ReactTooltip
      id={tooltipId}
      place="top"
      delayShow={100} // shows faster
      variant="light"
      style={{
        backgroundColor: "#f1f9ff",
        color: "#0d47a1",
        borderRadius: "6px",
        padding: "10px",
        fontSize: "16px",
        zIndex: 9999,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      }}
    >
     <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" }}>
  <tbody>
    <tr>
      <td style={{ padding: "4px 8px", fontWeight: "bold" , fontSize: "18px",textAlign:'left'}}>Principle</td>
      <td style={{ padding: "4px 8px", textAlign: "right", fontSize: "18px" ,textAlign:'left'}}>
        {principleVal ? `₹${formatNumberWithCommas(principleVal)}` : "-"}
      </td>
    </tr>
    <tr>
      <td style={{ padding: "4px 8px", fontWeight: "bold", fontSize: "18px",textAlign:'left' }}>Interest</td>
      <td style={{ padding: "4px 8px", textAlign: "right", fontSize: "18px",textAlign:'left' }}>
        {interestVal ? `₹${formatNumberWithCommas(interestVal)}` : "-"}
      </td>
    </tr>
  </tbody>
</table>

    </ReactTooltip>
  );
};

const [openRowDate, setOpenRowDate] = useState(null);
const [instParCount,setInstParCount] = useState([]);
const [isLoadingCount, setIsLoadingCount] = useState(false); 
const [isOpenCount, setIsOpenCount] = useState(false);
const [openingArrears,setOpeningArrears]=useState(null);
const openCountRow = (item) => {
  setOpeningArrears(item?.opening_arrears)
  const isCurrentlyOpen = openRowDate === item?.month;
  const formatMonthForAPI = (monthStr) => {
    const [year, monthText] = monthStr.split(" ");

    const months = {
      Jan: "01", Feb: "02", Mar: "03", Apr: "04",
      May: "05", Jun: "06", Jul: "07", Aug: "08",
      Sep: "09", Oct: "10", Nov: "11", Dec: "12"
    };

    return `${year}-${months[monthText]}-01`;
  };
  const formattedDate = formatMonthForAPI(item?.month);
  if (isCurrentlyOpen) {
    setOpenRowDate(null);
  } else {
    setOpenRowDate(item?.month);
    setIsOpenCount(!isOpenCount)
    setIsLoadingCount(true)
    let queryParams = {};

    if (productTypeSelected && productTypeSelected !== "all") {
      queryParams.loanType = Number(productTypeSelected);
    }
    axiosInstance.get(`/analytics/demand-collection-monthly?date=${formattedDate}`,
      {
        params: queryParams,
        headers: {
        },
      }

    )
      .then(function (res) {
        setInstParCount(res?.data?.data);
        setIsLoadingCount(false)
      })
      .catch(function (error) {
        setIsLoadingCount(false)
      });
  }
};


    return(
        <div style={{width:"100%",backgroundColor:'white',minHeight:'100vh'}}>

        <p style={{textAlign:"center",fontFamily:'Inter-Medium',color:'#667085',fontSize:'25px'}}>Demand Vs Collection</p>
       
         <div style={{ display: 'flex', justifyContent: 'flex-end', marginRight: '20px' }}>
  <div
    style={{
      backgroundColor: '#D22129',
      display: 'flex',
      alignItems: 'center',
      width: '130px',
      height: '34px',
      borderStyle: 'none',
      borderRadius: '5px',
      fontFamily: 'Inter-Medium',
      paddingLeft: '10px',
      justifyContent: 'space-around',
      cursor: 'pointer'
    }}
    id="downloadButton"
    onClick={() => handleDownloadList()}
  >
    <img src={download} style={{ height: '20px', marginLeft: '8px' }} alt=""/>
    <span style={{ fontFamily: 'Inter-Medium', color: 'white', paddingRight: '15px' }}>Export</span>
  </div>
    </div>

            
         {isLoading ? (
                  <Box style={{ display: 'flex',alignItems:'center',justifyContent:'center',padding:'15%'}}>
                  <CircularProgress />
                </Box>
                ):(<>
                 <div className='tables' style={{ marginTop: '20px' }}>
        
               <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}  >
                    <thead className='table-heading'>
                  
                        <tr>
                            <th style={{  fontSize: '14px',width:'7%',fontFamily:"Inter",padding:'12px 16px', textAlign:'left', whiteSpace:'nowrap'}}>Month</th>
                            <th style={{  fontSize: '14px',width:'10%',fontFamily:"Inter",padding:'12px 16px', textAlign:'left', whiteSpace:'nowrap'}}>Arrears</th>
                            <th style={{  fontSize: '14px',width:'10%',fontFamily:"Inter",padding:'12px 16px', textAlign:'left', whiteSpace:'nowrap'}}>Curr. Demand</th>
                            <th style={{  fontSize: '14px',width:'10%',fontFamily:"Inter",padding:'12px 16px', textAlign:'left', whiteSpace:'nowrap'}}>Tot. Demand</th>
                            <th style={{  fontSize: '14px',width:'11%',fontFamily:"Inter",padding:'12px 16px', textAlign:'left', whiteSpace:'nowrap'}}>Curr. Collection</th>
                            <th style={{  fontSize: '14px',width:'10%',fontFamily:"Inter",padding:'12px 16px', textAlign:'left', whiteSpace:'nowrap'}}>Arrear Collection</th>
                            <th style={{  fontSize: '14px',width:'10%',fontFamily:"Inter",padding:'12px 16px', textAlign:'left', whiteSpace:'nowrap'}}>Prepayments</th>
                            <th style={{  fontSize: '14px',width:'11%',fontFamily:"Inter",padding:'12px 16px', textAlign:'left', whiteSpace:'nowrap'}}>Total Collection</th>
                            <th style={{  fontSize: '14px',width:'12%',fontFamily:"Inter",padding:'12px 16px', textAlign:'left', whiteSpace:'nowrap'}}>Repayment Rate(%)</th>
                          
                        </tr>
                    </thead>
                    <tbody className='table-body'>
                      <>
            {latestDemandReports.map((item, index, arr) => {
                const isThisRowOpen = openRowDate === item?.month;
                return (
                  <React.Fragment key={index}>
                  <tr key={index} className="table-row" style={{ lineHeight: "1.3rem" ,cursor:"pointer"}} onClick={() => openCountRow(item)}>
                    <td style={{ ...baseParentCellStyle, width: "10em" }}>
                      {item.month}
                    </td>
                    <td
                      onClick={(e) => {
                          e.stopPropagation();
                          handleArrearsClick(index, "opening_arrears",item);
                        }}
                      onDoubleClick={(e) => {
                          e.stopPropagation();
                          setSelectedCell({ row: null, col: null });
                        }}
                      style={{
                        ...baseParentCellStyle,
                        width: "10em",
                        cursor: "pointer",
                        backgroundColor:
                          selectedCell.row === index && selectedCell.col === "opening_arrears"
                            ? "#cce5ff"
                            : "transparent",
                        ...interactiveCellBorderStyle,
                      }}
                    >
                                  <span
                          data-tooltip-id={`tooltip-${index}-opening`}
                          style={{ cursor: "pointer" }}
                        >
                          {item.opening_arrears
                            ? `₹${formatNumberWithCommas(item.opening_arrears)}`
                            : "-"}
                        </span>

                        {renderBreakdownTooltip(item, "opening_arrears", `tooltip-${index}-opening`)}
                    </td>


                      <td
                        onClick={(e) => {
                                e.stopPropagation();
                                handleArrearsClick(index, "currentDemand",item);
                              }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          setSelectedCell({ row: null, col: null });
                        }}

                        style={{
                          ...baseParentCellStyle,
                          width: "10em",
                          cursor: "pointer",
                          backgroundColor:
                            selectedCell.row === index && selectedCell.col === "currentDemand"
                              ? "#cce5ff"
                              : "transparent",
                          ...interactiveCellBorderStyle,
                        }}
                      >
                        
                      <span
                          data-tooltip-id={`tooltip-${index}-demand`}
                          style={{ cursor: "pointer" }}
                        > {item.currentDemand
                          ? `₹${formatNumberWithCommas(item.currentDemand)}`
                          : "-"}</span>

                            {renderBreakdownTooltip(item, "currentDemand", `tooltip-${index}-demand`)}

                        
                      </td>

                    <td style={{ ...baseParentCellStyle, width: '10em' }}>  <span
                          data-tooltip-id={`tooltip-${index}-totaldemand`}
                          style={{ cursor: "pointer" }}
                        >  ₹{formatNumberWithCommas(parseFloat(item.opening_arrears ? item.opening_arrears : 0) + parseFloat(item.currentDemand ? item.currentDemand : 0))}  {renderBreakdownTooltip(item, "totaldemand", `tooltip-${index}-totaldemand`)}</span> </td>
                    <td    onClick={(e) => {
                              e.stopPropagation();
                              handleArrearsClick(index, "currentDemand",item);
                            }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          setSelectedCell({ row: null, col: null });
                        }}

                        style={{
                          ...baseParentCellStyle,
                          width: "10em",
                          cursor: "pointer",
                          backgroundColor:
                            selectedCell.row === index && selectedCell.col === "currentDemand"
                              ? "#cce5ff"
                              : "transparent",
                          ...interactiveCellBorderStyle,
                        }} > <span
                          data-tooltip-id={`tooltip-${index}-collection`}
                          style={{ cursor: "pointer" }}
                        > {item.currentCollection ? <>₹{formatNumberWithCommas(item.currentCollection)}</> : '-'}</span>{renderBreakdownTooltip(item, "currentCollection", `tooltip-${index}-collection`)}</td>
                    <td    onClick={(e) => {
                              e.stopPropagation();
                              handleArrearsClick(index, "arrearCollection",item);
                            }}
                      onDoubleClick={(e) => {
                          e.stopPropagation();
                          setSelectedCell({ row: null, col: null });
                        }}
                      style={{
                        ...baseParentCellStyle,
                        width: "10em",
                        cursor: "pointer",
                        backgroundColor:
                          selectedCell.row === index && selectedCell.col === "arrearCollection"
                            ? "#cce5ff"
                            : "transparent",
                        ...interactiveCellBorderStyle,
                      }}><span
                          data-tooltip-id={`tooltip-${index}-arrearCollection`}
                          style={{ cursor: "pointer" }}
                        >{item?.arrearCollection? <>₹{formatNumberWithCommas(item?.arrearCollection )}</> : '-'}</span>{renderBreakdownTooltip(item, "arrearCollection", `tooltip-${index}-arrearCollection`)}</td>
                    <td  onClick={(e) => {
                              e.stopPropagation();
                              handleArrearsClick(index, "Prepayments",item);
                            }}
                      onDoubleClick={(e) => {
                          e.stopPropagation();
                          setSelectedCell({ row: null, col: null });
                        }}
                      style={{
                        fontSize: "14px",
                        width: "10em",
                        fontFamily: "Inter",
                        color: "#667085",

                        cursor: "pointer",
                        backgroundColor:
                          selectedCell.row === index && selectedCell.col === "Prepayments"
                            ? "#cce5ff"
                            : "transparent",
                        borderTop: "none",
                        borderBottom: "1px solid #d1d5db",
                        borderLeft: "none",
                        borderRight: "none",
                      }}><span
                          data-tooltip-id={`tooltip-${index}-Prepayments`}
                          style={{ cursor: "pointer",marginLeft:'20px' }}
                        >{item?.prePayments ? <>₹{formatNumberWithCommas(item?.prePayments)}</> : '-'}</span>{renderBreakdownTooltip(item, "Prepayments", `tooltip-${index}-Prepayments`)}</td>
                        
                    <td
                      onClick={(e) => {
                              e.stopPropagation();
                              handleArrearsClick(index, "totalCollection",item);
                            }}
                      onDoubleClick={(e) => {
                          e.stopPropagation();
                          setSelectedCell({ row: null, col: null });
                        }}
                      style={{
                        fontSize: '14px',
                        width: '10em',
                        fontFamily: "Inter",
                        color: '#667085',
                        cursor: "pointer",
                        backgroundColor:
                          selectedCell.row === index && selectedCell.col === "totalCollection"
                            ? "#cce5ff"
                            : "transparent",
                        borderTop: "none",
                        borderBottom: "1px solid #d1d5db",
                        borderLeft: "none",
                        borderRight: "none",
                      }}
                    >
                      <span
                          data-tooltip-id={`tooltip-${index}-totalCollectiondemand`}
                          style={{ cursor: "pointer" ,marginLeft:'20px'}}
                        >{item?.totalCollection ? <>₹{formatNumberWithCommas(item?.totalCollection!=="NaN[object Object]"?item?.totalCollection:'  -')}</> : '-'}</span>{renderBreakdownTooltip(item, "totalCollectiondemand", `tooltip-${index}-totalCollectiondemand`)}
                    </td>
                    <td style={{ ...baseParentCellStyle, width: '10em' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '18px', paddingRight: '12px', width: '100%' }}>
                        <span>
                          {item?.currentRepaymentRate ? parseFloat(item?.currentRepaymentRate).toFixed(2) : '-'}
                          {(() => {
                            const totalDemand = (Number(item?.opening_arrears) || 0) + (Number(item?.currentDemand) || 0);
                            const collected = (Number(item?.arrearCollection) || 0) + (Number(item?.currentCollection) || 0);
                            if (!totalDemand) return ' (-)';
                            return ` (${((collected / totalDemand) * 100).toFixed(2)}%)`;
                          })()}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                          {isThisRowOpen ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                        </span>
                      </div>
                    </td>
                  </tr>
                  {isLoadingCount && isThisRowOpen ? (
                    <tr>
                    <td colSpan="9" style={{ padding: '0' }}>
                    <Box style={{ display: 'flex',alignItems:'center',justifyContent:'center',padding:'15%'}}>
                      <CircularProgress />
                    </Box>
                    </td>
                    </tr>
                    
                  ):(
                    <>
                    {isThisRowOpen && (
                      <tr>
                        <td colSpan="9" style={{ padding: '0', border: 'none' }}>
                          <div
                            style={{
                              margin: '10px 40px 20px 40px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 6px 20px rgba(16,24,40,0.08)',
                              background: '#fff',
                              overflow: 'hidden',
                            }}
                          >
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead style={{ background: '#f8fafc' }}>
                                <tr>
                                  {[
                                    'Day',
                                    'Arrears',
                                    'Curr. Demand',
                                    'Tot. Demand',
                                    'Curr. Collection',
                                    'Arrear Collection',
                                    'Prepayments',
                                    'Total Collection',
                                  ].map((label) => (
                                    <th
                                      key={label}
                                      style={{
                                        fontSize: '0.85vw',
                                        fontFamily: 'Inter-Medium',
                                        color: '#475467',
                                        padding: '10px',
                                        textAlign: 'left',
                                        borderBottom: '1px solid #e5e7eb',
                                        lineHeight: '1.3rem',
                                      }}
                                    >
                                      {label}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {instParCount?.map((countItem, idx) => (
                                  <tr
                                    key={`${item.month}-${idx}`}
                                    style={{
                                      borderBottom: '1px solid #f1f5f9',
                                      lineHeight: '1.3rem',
                                    }}
                                  >
                                    <td style={{ padding: '10px 16px', fontSize: '14px', color: '#667085' }}>
                                      {countItem.day || '0'}
                                    </td>
                                    <td style={{ padding: '10px 16px', fontSize: '14px', color: '#667085' }}>
                                      {countItem.opening_arrears
                                        ? `₹${formatNumberWithCommas(openingArrears)}`
                                        : '0'}
                                    </td>
                                    <td style={{ padding: '10px 16px', fontSize: '14px', color: '#667085' }}>
                                      {countItem.currentDemand
                                        ? `₹${formatNumberWithCommas(countItem.currentDemand)}`
                                        : '0'}
                                    </td>
                                    <td style={{ padding: '10px 16px', fontSize: '14px', color: '#667085' }}>
                                      {countItem.totalDemand
                                        ? `₹${formatNumberWithCommas(openingArrears + countItem.currentDemand)}`
                                        : '0'}
                                    </td>
                                    <td style={{ padding: '10px 16px', fontSize: '14px', color: '#667085' }}>
                                      {countItem.currentCollection
                                        ? `₹${formatNumberWithCommas(countItem.currentCollection)}`
                                        : '0'}
                                    </td>
                                    <td style={{ padding: '10px 16px', fontSize: '14px', color: '#667085' }}>
                                      {countItem.arrearCollection
                                        ? `₹${formatNumberWithCommas(countItem.arrearCollection)}`
                                        : '0'}
                                    </td>
                                    <td style={{ padding: '10px 16px', fontSize: '14px', color: '#667085' }}>
                                      {countItem.prepaid
                                        ? `₹${formatNumberWithCommas(countItem.prepaid)}`
                                        : '0'}
                                    </td>
                                    <td style={{ padding: '10px 16px', fontSize: '14px', color: '#667085' }}>
                                      {countItem.totalCollection
                                        ? `₹${formatNumberWithCommas(countItem.totalCollection)}`
                                        : '0'}
                                    </td>
                                    {/* <td style={{ padding: '10px 16px', fontSize: '14px', color: '#667085' }}>
                                      {countItem?.currentRepaymentRate
                                        ? parseFloat(countItem.currentRepaymentRate).toFixed(2)
                                        : '0'}
                                    </td> */}
                                  </tr>
                                ))}
                                {(!instParCount || instParCount.length === 0) && (
                                  <tr>
                                    <td
                                      colSpan="8"
                                      style={{
                                        padding: '20px',
                                        fontSize: '14px',
                                        color: '#98a2b3',
                                        textAlign: 'center',
                                        lineHeight: '1.3rem',
                                      }}
                                    >
                                      No day-wise data available.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}

                    </>
                  )}
                  </React.Fragment>
                );
              })}
            </>



        </tbody>
                </table>

                 </div>
       
                </>)
        
}


<div style={{ display: 'flex', justifyContent: 'flex-end', marginRight: '20px' }}>
{( (selectedCell.col === "opening_arrears" || selectedCell.col === "arrearCollection" || selectedCell.col === "currentDemand" || selectedCell.col === "Prepayments" ) 
     && paginatedData.length > 0 ) && (
    <div
      style={{
        backgroundColor: '#D22129',
        display: 'flex',
        alignItems: 'center',
        width: '130px',
        height: '34px',
        borderStyle: 'none',
        borderRadius: "5px",
        fontFamily: "Inter-Medium",
        paddingLeft: '10px',
        justifyContent: 'space-around',
        cursor: 'pointer',
        marginLeft: '20px',
        marginTop: '30px'
      }}
      id="downloadButton"
      onClick={handleDownloadCollectionList}
    >
      <img src={download} style={{ height: '20px', marginLeft: '8px' }} alt="" />
      <span style={{ fontFamily: "Inter-Medium", color: 'white', paddingRight: "15px" }}>
        Export
      </span>
    </div>
  )}
</div>

 {isLoadingTable ? (
                  <Box style={{ display: 'flex',alignItems:'center',justifyContent:'center',padding:'15%'}}>
                  <CircularProgress />
                </Box>
                ):(<>

{["opening_arrears","arrearCollection"].includes(selectedCell.col) && apiData.length > 0 && (
        <div style={{marginTop:'30px'}}>
      <table className='tables'>
      <thead className='table-heading' >
            <tr>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '8px%', fontFamily: "Inter-Medium", paddingLeft: '10px' }}>LoanId</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '10px%', fontFamily: "Inter-Medium",paddingLeft: '10px' }}>Name</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '7px%', fontFamily: "Inter-Medium" }}>Mobile</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '15px%', fontFamily: "Inter-Medium" ,paddingLeft: '10px' }}>OpeningArrears</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '10px%', fontFamily: "Inter-Medium" ,paddingLeft: '10px' }}>ArrearCollection</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '10px%', fontFamily: "Inter-Medium",paddingLeft: '10px'  }}>Bucket</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '10px%', fontFamily: "Inter-Medium" ,paddingLeft: '10px' }}>status</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '10px%', fontFamily: "Inter-Medium" ,paddingLeft: '10px' }}>PaidBucket</th>
      
            </tr>
          </thead>
        <tbody className="table-body">
          {paginatedData?.map((item, index) => (
             <tr className="table-row" key={index} style={{ lineHeight:"25px" ,cursor: "pointer"}}
      
             >
               <td 
               style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085', paddingLeft: '10px' }}
                onClick={() => handleLoanProfile(item.loan_id,item.legacy_loan_id)}
                >{item.legacy_loan_id}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>{item.userName}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '7em', fontFamily: "Inter-Medium", color: '#667085' }}>{item.mobile}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>₹{formatNumberWithCommas(Math.round(item.opening_arrears))}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>₹{formatNumberWithCommas(Math.round(item.arrearCollection))}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>{item.bucketing?item.bucketing:'-'}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>{item.status_name?item.status_name:'-'}</td>
                 <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>{item.paidBucket?item.paidBucket:'-'}</td>
  
             </tr>
          ))}
        </tbody>
      </table>
    </div>)}
</>)}


 {isLoadingTable ? (
                  <Box style={{ display: 'flex',alignItems:'center',justifyContent:'center',padding:'15%'}}>
                  <CircularProgress />
                </Box>
                ):(<>

 {selectedCell.col === "currentDemand" && apiData.length > 0 && (
        <div style={{marginTop:'30px'}}>
      <table className='tables'>
      <thead className='table-heading' >
            <tr>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '8px%', fontFamily: "Inter-Medium", paddingLeft: '10px' }}>LoanId</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '10px%', fontFamily: "Inter-Medium",paddingLeft: '10px' }}>Name</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '7px%', fontFamily: "Inter-Medium" }}>Mobile</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '15px%', fontFamily: "Inter-Medium" ,paddingLeft: '10px' }}>Demand</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '10px%', fontFamily: "Inter-Medium" ,paddingLeft: '10px' }}>Paid emiamount</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '10px%', fontFamily: "Inter-Medium",paddingLeft: '10px'  }}>Bucket</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '10px%', fontFamily: "Inter-Medium" ,paddingLeft: '10px' }}>status</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '10px%', fontFamily: "Inter-Medium" ,paddingLeft: '10px' }}>PaidBucket</th>
      
            </tr>
          </thead>
        <tbody className="table-body">
          {paginatedData?.map((item, index) => (
             <tr className="table-row" key={index} style={{ lineHeight:"25px" ,cursor: "pointer"}}
      
             >
               <td 
               style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085', paddingLeft: '10px' }}
                onClick={() => handleLoanProfile(item.loan_id,item.legacy_loan_id)}
                >{item.legacy_loan_id}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>{item.userName}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '7em', fontFamily: "Inter-Medium", color: '#667085' }}>{item.mobile}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>₹{formatNumberWithCommas(Math.round(item.currentDemand))}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>₹{formatNumberWithCommas(Math.round(item.paid_emi_amount))}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>{item.bucketing?item.bucketing:'-'}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>{item.status_name?item.status_name:'-'}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>{item.paidBucket?item.paidBucket:'-'}</td>
  
             </tr>
          ))}
        </tbody>
      </table>
    </div>)}
    </>)}

 {isLoadingTable ? (
                  <Box style={{ display: 'flex',alignItems:'center',justifyContent:'center',padding:'15%'}}>
                  <CircularProgress />
                </Box>
                ):(<>

 {selectedCell.col === "Prepayments" && apiData.length > 0 && (
        <div style={{marginTop:'30px'}}>
      <table className='tables'>
      <thead className='table-heading' >
            <tr>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '8px%', fontFamily: "Inter-Medium", paddingLeft: '10px' }}>LoanId</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '10px%', fontFamily: "Inter-Medium",paddingLeft: '10px' }}>Name</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '7px%', fontFamily: "Inter-Medium" }}>Mobile</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '15px%', fontFamily: "Inter-Medium" ,paddingLeft: '10px' }}>Prepaid</th>
            </tr>
          </thead>
        <tbody className="table-body">
          {paginatedData?.map((item, index) => (
             <tr className="table-row" key={index} style={{ lineHeight:"25px" ,cursor: "pointer"}}
      
             >
               <td 
               style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085', paddingLeft: '10px' }}
                onClick={() => handleLoanProfile(item.loan_id,item.legacy_loan_id)}
                >{item.legacy_loan_id}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>{item.userName}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '7em', fontFamily: "Inter-Medium", color: '#667085' }}>{item.mobile}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>₹{formatNumberWithCommas(Math.round(item.prepaid))}</td>
     
  
             </tr>
          ))}
        </tbody>
      </table>
    </div>)}
    </>)}
 {isLoadingTable ? (
                  <Box style={{ display: 'flex',alignItems:'center',justifyContent:'center',padding:'15%'}}>
                  <CircularProgress />
                </Box>
                ):(<>

 {selectedCell.col === "totalCollection" && apiData.length > 0 && (
        <div style={{marginTop:'30px'}}>
      <table className='tables'>
      <thead className='table-heading' >
            <tr>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '8px%', fontFamily: "Inter-Medium", paddingLeft: '10px' }}>LoanId</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '10px%', fontFamily: "Inter-Medium",paddingLeft: '10px' }}>Name</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '7px%', fontFamily: "Inter-Medium" }}>Mobile</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '10px%', fontFamily: "Inter-Medium" ,paddingLeft: '10px' }}>Curr. Collection</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '10px%', fontFamily: "Inter-Medium" ,paddingLeft: '10px' }}>Arrear Collection</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '10px%', fontFamily: "Inter-Medium" ,paddingLeft: '10px' }}>Prepayment</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '10px%', fontFamily: "Inter-Medium" ,paddingLeft: '10px' }}>Total Collection</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '10px%', fontFamily: "Inter-Medium",paddingLeft: '10px'  }}>Bucket</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '10px%', fontFamily: "Inter-Medium" ,paddingLeft: '10px' }}>status</th>
              <th style={{ fontSize: '14px', cursor: 'pointer', width: '10px%', fontFamily: "Inter-Medium" ,paddingLeft: '10px' }}>PaidBucket</th>
            </tr>
          </thead>
        <tbody className="table-body">
          {paginatedData?.map((item, index) => {
            const currentCollection = toSafeNumber(item?.paid_emi_amount);
            const arrearCollection = toSafeNumber(item?.arrearCollection);
            const prepaymentCollection = toSafeNumber(item?.prepaid);
            const totalCollection =
              currentCollection + arrearCollection + prepaymentCollection;

            return (
             <tr className="table-row" key={index} style={{ lineHeight:"25px" ,cursor: "pointer"}}>
               <td 
               style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085', paddingLeft: '10px' }}
                onClick={() => handleLoanProfile(item.loan_id,item.legacy_loan_id)}
                >{item.legacy_loan_id || '-'}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>{item.userName || '-'}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '7em', fontFamily: "Inter-Medium", color: '#667085' }}>{item.mobile || '-'}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>
                ₹{formatNumberWithCommas(Math.round(currentCollection))}
               </td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>
                ₹{formatNumberWithCommas(Math.round(arrearCollection))}
               </td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>
                ₹{formatNumberWithCommas(Math.round(prepaymentCollection))}
               </td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>
                ₹{formatNumberWithCommas(Math.round(totalCollection))}
               </td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>{item.bucketing?item.bucketing:'-'}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>{item.status_name?item.status_name:'-'}</td>
               <td style={{ fontSize: '14px', cursor: 'pointer', width: '10em', fontFamily: "Inter-Medium", color: '#667085' }}>{item.paidBucket?item.paidBucket:'-'}</td>
             </tr>
          )})}
        </tbody>
      </table>
    </div>)}
    </>)}
  {( (["opening_arrears","arrearCollection","currentDemand","Prepayments","totalCollection"].includes(selectedCell.col)) 
     && paginatedData.length > 0 ) && (
   <TablePagination
      rowsPerPageOptions={[50, { value: -1, label: "All" }]} 
      component="div"
      count={apiData.length}
      rowsPerPage={rowsPerPage}
      page={page}
      onPageChange={(event, newPage) => setPage(newPage)}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // reset to first page
      }}
      sx={{
        "& .MuiTablePagination-select": { marginBottom: "10px" },
        "& .MuiIconButton-root": { marginBottom: "10px" }
      }}
    /> )}

        </div>

    )
}

export default DemandReports;